import React, { useCallback, useEffect, useRef, useState } from "react";
import FormLabel from "../../../Utilities/FormLabel";
import config from "../../../../config/config.json";
import { apiCall } from "../../../../_services/apiCall";
import TableTypeToggles from "./TableTypeToggles";
import RowsConfiguration from "./RowsConfiguration";
import GlobalColumns from "./GlobalColumns";

const API_BASE = config.ADMIN_API_URL;

// ── Table mode constants ───────────────────────────────────────────────────────
const fixedRowsFixedColumns   = "fixedRowsFixedColumns";
const fixedRowsDynamicColumns = "fixedRowsDynamicColumns";
const dynamicRowsFixedColumns = "dynamicRowsFixedColumns";
const dynamicRowsDynamicColumns = "dynamicRowsDynamicColumns";

// ── TableSchemaBuilder ────────────────────────────────────────────────────────
// Orchestrator: owns all state, effects, handlers, and schema serialisation.
// Delegates every visible section to a focused child component.
export default function TableSchemaBuilder({
  tableSchemaForEdit,
  setTableSchema = () => {},
  restrictToYesNo = false,
}) {
  // Single state variable encodes all four configurations.
  const [tableMode, setTableMode] = useState(fixedRowsFixedColumns);
  const [rows, setRows] = useState(restrictToYesNo ? [{ label: "", columns: [] }] : []);
  const [columns, setColumns] = useState([]);
  const [predefinedRows, setPredefinedRows] = useState([]);
  const [predefinedColumns, setPredefinedColumns] = useState([]);
  const [unitMasters, setUnitMasters] = useState([]);

  // ── Fetch unit masters on mount ────────────────────────────────────────────
  useEffect(() => {
    apiCall(`${API_BASE}getUnitMaster`, {}, {}, "GET").then((res) => {
      const data = res.data?.data || res.data || [];
      setUnitMasters(Array.isArray(data) ? data : []);
    });
  }, []);

  // ── Schema cache — keyed by tableMode constant, populated on mount from prop ──
  const schemaCache = useRef({});

  // ── Init from edit + populate schema cache ────────────────────────────────
  useEffect(() => {
    if (!tableSchemaForEdit || tableSchemaForEdit.length === 0) return;
    const initCols = (cols) =>
      (cols || []).map((c) => ({
        ...c,
        data_type: c.data_type || "string",
        dropdown_options: Array.isArray(c.dropdown_options) ? c.dropdown_options : [],
      }));
    const clone = (val) => JSON.parse(JSON.stringify(val));

    if (tableSchemaForEdit.type === "yes_no") {
      const parsedRows = [{ label: tableSchemaForEdit.row?.label || "", columns: [] }];
      const parsedCols = initCols(tableSchemaForEdit.columns);
      setTableMode(fixedRowsFixedColumns);
      setRows(parsedRows);
      setColumns(parsedCols);
      schemaCache.current[fixedRowsFixedColumns] = { rows: clone(parsedRows), columns: clone(parsedCols)};

    } else if (tableSchemaForEdit.type === "fully-dynamic") {
      setTableMode(dynamicRowsDynamicColumns);
      const parsedPredefinedRows = Array.isArray(tableSchemaForEdit.pre_defined_rows) ? tableSchemaForEdit.pre_defined_rows : [];
      const parsedPredefinedColumns = Array.isArray(tableSchemaForEdit.pre_defined_columns)
        ? tableSchemaForEdit.pre_defined_columns.map((c) => ({
            ...c,
            data_type: c.data_type || "string",
            dropdown_options: Array.isArray(c.dropdown_options) ? c.dropdown_options : [],
          }))
        : [];
      setPredefinedRows(parsedPredefinedRows);
      setPredefinedColumns(parsedPredefinedColumns);
      schemaCache.current[dynamicRowsDynamicColumns] = { rows: [], columns: [], predefinedRows: clone(parsedPredefinedRows), predefinedColumns: clone(parsedPredefinedColumns) };

    } else if (tableSchemaForEdit.type === "dynamic") {
      const parsedCols = initCols(tableSchemaForEdit.columns);
      const parsedPredefinedRows = Array.isArray(tableSchemaForEdit.pre_defined_rows) ? tableSchemaForEdit.pre_defined_rows : [];
      setTableMode(dynamicRowsFixedColumns);
      setColumns(parsedCols);
      setPredefinedRows(parsedPredefinedRows);
      schemaCache.current[dynamicRowsFixedColumns] = { rows: [], columns: clone(parsedCols), predefinedRows: clone(parsedPredefinedRows) };


    } else if (tableSchemaForEdit.type === "fixed") {
      if (tableSchemaForEdit.mode === "same-columns") {
        const parsedRows = tableSchemaForEdit.rows || [];
        const parsedCols = initCols(tableSchemaForEdit.columns);
        setTableMode(fixedRowsFixedColumns);
        setRows(parsedRows);
        setColumns(parsedCols);
        schemaCache.current[fixedRowsFixedColumns] = { rows: clone(parsedRows), columns: clone(parsedCols), };
      } else {
        const parsedRows = (tableSchemaForEdit.rows || []).map((r) => ({
          ...r,
          columns: initCols(r.columns),
        }));
        const parsedPredefinedColumns = Array.isArray(tableSchemaForEdit.pre_defined_columns)
          ? initCols(tableSchemaForEdit.pre_defined_columns)
          : [];
        setTableMode(fixedRowsDynamicColumns);
        setRows(parsedRows);
        setPredefinedColumns(parsedPredefinedColumns);
        schemaCache.current[fixedRowsDynamicColumns] = { rows: clone(parsedRows), columns: [], predefinedColumns: clone(parsedPredefinedColumns) };
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (restrictToYesNo && rows.length === 0) setRows([{ label: "", columns: [] }]);
  }, [restrictToYesNo, rows.length]);

  // ── Row handlers ───────────────────────────────────────────────────────────
  const addRow = () => setRows([...rows, { label: "", columns: [] }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRowLabel = (i, val) =>
    setRows(rows.map((r, idx) => (idx === i ? { ...r, label: val } : r)));

  // ── Global column handlers ─────────────────────────────────────────────────
  const addColumn = () => setColumns([...columns, { label: "", data_type: "string", dropdown_options: [] }]);
  const removeColumn = (i) => setColumns(columns.filter((_, idx) => idx !== i));
  const updateColumn = (i, field, value) =>
    setColumns(columns.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));

  // ── Per-row column handlers ────────────────────────────────────────────────
  const addColumnToRow = (ri) =>
    setRows(rows.map((r, idx) =>
      idx === ri ? { ...r, columns: [...r.columns, { label: "", data_type: "string", dropdown_options: [] }] } : r
    ));
  const removeColumnFromRow = (ri, ci) =>
    setRows(rows.map((r, idx) =>
      idx === ri ? { ...r, columns: r.columns.filter((_, i) => i !== ci) } : r
    ));
  const updateRowColumn = (ri, ci, field, value) =>
    setRows(rows.map((r, idx) =>
      idx === ri
        ? { ...r, columns: r.columns.map((c, i) => (i === ci ? { ...c, [field]: value } : c)) }
        : r
    ));

  // ── Predefined rows handlers ────────────────────────────────────────────────
  const addPredefinedRow = () => setPredefinedRows([...predefinedRows, { label: "" }]);
  const removePredefinedRow = (i) => setPredefinedRows(predefinedRows.filter((_, idx) => idx !== i));
  const updatePredefinedRowLabel = (i, val) => setPredefinedRows(predefinedRows.map((r, idx) => (idx === i ? { ...r, label: val } : r)));

  // ── Predefined columns handlers ─────────────────────────────────────────────
  const addPredefinedColumn = () => setPredefinedColumns([...predefinedColumns, { label: "", data_type: "string", dropdown_options: [] }]);
  const removePredefinedColumn = (i) => setPredefinedColumns(predefinedColumns.filter((_, idx) => idx !== i));
  const updatePredefinedColumn = (i, field, value) => setPredefinedColumns(predefinedColumns.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));

  // ── Cache restore helper ───────────────────────────────────────────────────
  const restoreFromCache = useCallback((mode) => {
    const cached = schemaCache.current[mode];
    if (cached) {
      setRows(JSON.parse(JSON.stringify(cached.rows)));
      setColumns(JSON.parse(JSON.stringify(cached.columns)));
      setPredefinedRows(JSON.parse(JSON.stringify(cached.predefinedRows ?? [])));
      setPredefinedColumns(JSON.parse(JSON.stringify(cached.predefinedColumns ?? [])));
    } else {
      setRows([]);
      setColumns([]);
      setPredefinedRows([]);
      setPredefinedColumns([]);
    }
  }, []);

  // ── Mode transition handlers ───────────────────────────────────────────────
  const onSelectFixedRows = () => {
    const newMode = tableMode === dynamicRowsDynamicColumns ? fixedRowsDynamicColumns : fixedRowsFixedColumns;
    schemaCache.current[tableMode] = { rows, columns, predefinedRows, predefinedColumns };
    setTableMode(newMode);
    restoreFromCache(newMode);
  };
  const onSelectDynamicRows = () => {
    const newMode = tableMode === fixedRowsDynamicColumns ? dynamicRowsDynamicColumns : dynamicRowsFixedColumns;
    schemaCache.current[tableMode] = { rows, columns, predefinedRows, predefinedColumns };
    setTableMode(newMode);
    restoreFromCache(newMode);
  };
  const onSelectFixedColumns = () => {
    const newMode = tableMode === fixedRowsDynamicColumns || tableMode === fixedRowsFixedColumns ? fixedRowsFixedColumns : dynamicRowsFixedColumns;
    schemaCache.current[tableMode] = { rows, columns, predefinedRows, predefinedColumns };
    setTableMode(newMode);
    restoreFromCache(newMode);
  };
  const onSelectDynamicColumns = () => {
    const newMode = tableMode === fixedRowsFixedColumns || tableMode === fixedRowsDynamicColumns ? fixedRowsDynamicColumns : dynamicRowsDynamicColumns;
    schemaCache.current[tableMode] = { rows, columns, predefinedRows, predefinedColumns };
    setTableMode(newMode);
    restoreFromCache(newMode);
  };

  // ── Schema builder ─────────────────────────────────────────────────────────
  const buildSchema = useCallback(() => {
    const mapCol = ({ label, data_type = "string", dropdown_options = [] }) => ({
      label,
      data_type,
      dropdown_options: (data_type === "dropdown" || data_type === "number") ? dropdown_options : undefined,
    });
    if (restrictToYesNo)
      return { type: "yes_no", mode: "single-row-multiple-columns", row: { label: rows[0]?.label }, columns: columns.map(mapCol) };
    if (tableMode === dynamicRowsDynamicColumns) {
      const schema = { type: "fully-dynamic" };
      if (predefinedRows.length > 0) {
        schema.pre_defined_rows = predefinedRows.map(r => ({ label: r.label }));
      }
      if (predefinedColumns.length > 0) {
        schema.pre_defined_columns = predefinedColumns.map(mapCol);
      }
      return schema;
    }
    if (tableMode === dynamicRowsFixedColumns) {
      const schema = { type: "dynamic", columns: columns.map(mapCol) };
      if (predefinedRows.length > 0) schema.pre_defined_rows = predefinedRows.map(r => ({ label: r.label }));
      return schema;
    }
    if (tableMode === fixedRowsFixedColumns)
      return { type: "fixed", mode: "same-columns", rows: rows.map((r) => ({ label: r.label })), columns: columns.map(mapCol)};
    // fixedRowsDynamicColumns
    const schema = { type: "fixed", mode: "different-columns", rows: rows.map((row) => ({ label: row.label, columns: row.columns.map(mapCol) })) };
    if (predefinedColumns.length > 0) schema.pre_defined_columns = predefinedColumns.map(mapCol);
    return schema;
  }, [tableMode, rows, columns, predefinedRows, predefinedColumns, restrictToYesNo]);

  useEffect(() => { setTableSchema(buildSchema()); }, [buildSchema, setTableSchema]);

  // ── Derived visibility flags ───────────────────────────────────────────────
  const showFixedRows    = !restrictToYesNo && (tableMode === fixedRowsFixedColumns || tableMode === fixedRowsDynamicColumns);
  const showGlobalColumns = restrictToYesNo || tableMode === fixedRowsFixedColumns || tableMode === dynamicRowsFixedColumns;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="table-schema-builder">
      {/* Section header */}
      <div className="mb-3">
        <FormLabel icon="fas fa-table">
          {restrictToYesNo
            ? "Yes/No Response Configuration"
            : "Table Configuration"}
        </FormLabel>
        {restrictToYesNo && (
          <small className="d-block text-muted mt-1">
            Configure multiple columns for Yes/No responses
          </small>
        )}
      </div>

      {/* Table Type + Column Structure toggles */}
      {!restrictToYesNo && (
        <TableTypeToggles
          tableMode={tableMode}
          onSelectFixedRows={onSelectFixedRows}
          onSelectDynamicRows={onSelectDynamicRows}
          onSelectFixedColumns={onSelectFixedColumns}
          onSelectDynamicColumns={onSelectDynamicColumns}
        />
      )}

      {/* Attribute label for Yes/No */}
      {restrictToYesNo && (
        <div className="mb-3 p-3 border rounded bg-light">
          <label className="form-label small fw-bold text-muted mb-1">
            Attribute Label
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter attribute label"
            value={rows[0]?.label || ""}
            onChange={(e) => updateRowLabel(0, e.target.value)}
          />
        </div>
      )}

      {/* Rows Configuration — fixed-rows modes only */}
      {showFixedRows && (
        <RowsConfiguration
          rows={rows}
          dynamicColumns={tableMode === fixedRowsDynamicColumns}
          unitMasters={unitMasters}
          onAddRow={addRow}
          onRemoveRow={removeRow}
          onUpdateRowLabel={updateRowLabel}
          onAddColumnToRow={addColumnToRow}
          onRemoveColumnFromRow={removeColumnFromRow}
          onUpdateRowColumn={updateRowColumn}
        />
      )}

      {/* Predefined Rows (Optional) — dynamic rows modes */}
      {(tableMode === dynamicRowsDynamicColumns || tableMode === dynamicRowsFixedColumns) && (
        <div className="mb-4">
          <RowsConfiguration
            rows={predefinedRows}
            dynamicColumns={false}
            unitMasters={unitMasters}
            onAddRow={addPredefinedRow}
            onRemoveRow={removePredefinedRow}
            onUpdateRowLabel={updatePredefinedRowLabel}
          />
        </div>
      )}

      {/* Predefined Columns (Optional) — dynamic rows + dynamic columns, or fixed rows + dynamic columns */}
      {(tableMode === dynamicRowsDynamicColumns || tableMode === fixedRowsDynamicColumns) && (
        <div className="mb-4">
          <GlobalColumns
            columns={predefinedColumns}
            restrictToYesNo={false}
            unitMasters={unitMasters}
            onAddColumn={addPredefinedColumn}
            onRemoveColumn={removePredefinedColumn}
            onUpdateColumn={updatePredefinedColumn}
          />
        </div>
      )}

      {/* Global Columns — fixedRowsFixedColumns, dynamicRowsFixedColumns, and yes/no */}
      {showGlobalColumns && (
        <GlobalColumns
          columns={columns}
          restrictToYesNo={restrictToYesNo}
          unitMasters={unitMasters}
          onAddColumn={addColumn}
          onRemoveColumn={removeColumn}
          onUpdateColumn={updateColumn}
        />
      )}
    </div>
  );
}
